import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lightbulb, ArrowRight } from "lucide-react";

interface SmartRecommendationsProps {
  jobStatus: string;
  hasPhotos: boolean;
  hasMeasurements: boolean;
  hasEstimate: boolean;
  hasTasks: boolean;
  onNavigateToTab: (tab: string) => void;
  onOpenForm: (formType: string) => void;
}

export const SmartRecommendations = ({
  jobStatus,
  hasPhotos,
  hasMeasurements,
  hasEstimate,
  hasTasks,
  onNavigateToTab,
  onOpenForm,
}: SmartRecommendationsProps) => {
  const getRecommendations = () => {
    const recommendations = [];

    if (!hasPhotos) {
      recommendations.push({
        title: "Upload inspection photos",
        description: "Capture roof damage for analysis",
        action: () => onOpenForm('upload_photo'),
        priority: "high",
        icon: "📸"
      });
    }

    if (!hasMeasurements) {
      recommendations.push({
        title: "Order roof measurement",
        description: "Get accurate square footage",
        action: () => onNavigateToTab('Overview'),
        priority: "high",
        icon: "📐"
      });
    }

    if (hasPhotos && !hasEstimate) {
      recommendations.push({
        title: "Generate estimate",
        description: "Create pricing from damage analysis",
        action: () => onNavigateToTab('Estimates'),
        priority: "medium",
        icon: "💰"
      });
    }

    if (!hasTasks) {
      recommendations.push({
        title: "Create workflow tasks",
        description: "Set up job milestones",
        action: () => onOpenForm('create_task'),
        priority: "low",
        icon: "✅"
      });
    }

    if (hasEstimate && jobStatus === 'new') {
      recommendations.push({
        title: "Send proposal to customer",
        description: "Share estimate and get approval",
        action: () => onNavigateToTab('Proposals'),
        priority: "high",
        icon: "📄"
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();
  const topRecommendations = recommendations.slice(0, 3);

  return (
    <Card className="p-6 border-accent/20 bg-gradient-to-br from-accent/5 via-card to-warning/5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-warning flex items-center justify-center premium-glow">
          <Lightbulb className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-display font-bold">Smart Recommendations</h3>
          <p className="text-xs text-muted-foreground">AI-suggested next steps</p>
        </div>
      </div>

      <div className="space-y-3">
        {topRecommendations.length > 0 ? (
          topRecommendations.map((rec, idx) => (
            <div
              key={idx}
              className="group p-4 rounded-lg border border-border/50 bg-card/50 hover:bg-accent/5 hover:border-accent/30 transition-all cursor-pointer"
              onClick={rec.action}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{rec.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold truncate">{rec.title}</h4>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{rec.description}</p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-sm font-medium text-success">All caught up!</p>
            <p className="text-xs text-muted-foreground mt-1">
              No critical actions needed right now
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};
